import { z } from "zod";
import {
  Box,
  CategoricalBarChart,
  WidgetEmpty,
  WidgetSkeleton,
  Text,
  compactNumber,
  useCategoricalColors,
  useCategoryRowBudget,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
  truncateLabel,
} from "@salestrak/ui";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { WIDGET_ACCENT } from "../shared/widget-accents";
import { useMostActiveUsers, type MostActiveUserRow } from "./use-most-active-users";

export const mostActiveUsersConfigSchema = z
  .looseObject({
    limit: z.int().min(3).max(20).default(8),
  });

export type MostActiveUsersConfig = z.infer<typeof mostActiveUsersConfigSchema>;

/**
 * Members ranked by activity as horizontal bars against a shared count axis.
 * Activity is heavily skewed — a couple of heavy users and a long quiet tail —
 * so the axis is what carries the comparison: the tail rows sit at a hairline
 * beside a leader that runs the full width, and the ratio stays readable.
 * Rows orientation keeps the names horizontal; a person's name does not survive
 * being rotated onto a vertical axis.
 */
function MostActiveUsersBar({ rows, h }: { rows: MostActiveUserRow[]; h: number }) {
  // Members own no palette color, and each bar is already separated by its
  // position on the axis and its own tick — so the whole set takes one hue
  // through the ONE shared assignment. Cycling the ramp here would paint five
  // colors that stand for nothing.
  const sliceColors = useCategoricalColors(rows, {
    fallbackToken: WIDGET_ACCENT.mostActiveUsers,
  });
  const compact = h < 4;
  // The configured limit is a ceiling, not a promise: a short tile cannot name
  // eight people, and a chart handed more rows than it is tall stacks the names
  // on top of each other. The rendered height decides how many survive.
  const { ref, budget } = useCategoryRowBudget();
  const shown = budget === null ? rows : rows.slice(0, budget);
  const hidden = rows.length - shown.length;
  return (
    <Box direction="column" fill="flex" gap="xs">
      <Box fill="flex" ref={ref}>
        <CategoricalBarChart<MostActiveUserRow>
          data={shown}
          categoryKey="label"
          series={[{ key: "count" }]}
          orientation="rows"
          categoryColorTokens={sliceColors.tokens.slice(0, shown.length)}
          categoryAxisWidth={88}
          showGrid={!compact}
          showAxes={!compact}
          // A member bar is unreadable without the name beside it, so the
          // category axis stays even when the tile is too short for ticks.
          showCategoryAxis
          categoryTickFormatter={(v) => truncateLabel(v, 14)}
          valueTickFormatter={compactNumber}
          ariaLabel="Most active members"
          renderTooltip={(row) => (
            <WidgetChartTooltip
              label={row.label}
              details={`${row.count.toLocaleString()} ${row.count === 1 ? "action" : "actions"}`}
            />
          )}
        />
      </Box>
      {hidden > 0 ? (
        <Text size="xs" variant="muted" truncate>
          {`+${hidden.toLocaleString()} more`}
        </Text>
      ) : null}
    </Box>
  );
}

function MostActiveUsersRender({ h, config }: WidgetRenderContext<MostActiveUsersConfig>) {
  const { rows, loading } = useMostActiveUsers(config.limit);
  const emptyTitle = useWindowEmptyTitle("No activity");

  if (loading) return <WidgetSkeleton variant="bars" />;
  if (rows.length === 0) {
    return <WidgetEmpty title={emptyTitle} />;
  }

  return <MostActiveUsersBar rows={rows} h={h} />;
}

const mostActiveUsersSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      { key: "limit", kind: "number", label: "Max members", icon: "list", min: 3, max: 20, step: 1 },
    ],
  },
];

export const mostActiveUsersDef: WidgetModule<MostActiveUsersConfig> = {
  type: "most-active-users",
  title: "Most active members",
  icon: "activity",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 4 },
  configSchema: mostActiveUsersConfigSchema,
  settings: mostActiveUsersSettings,
  // No gallery card: a raw activity count rewards clicking around, and Team
  // members already ranks on Updates. Rows placed before the card left keep
  // rendering.
  catalog: [],
  render: MostActiveUsersRender,
};
